import { ChevronRight, RemoveRedEyeRounded } from "@mui/icons-material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { jiraIssuesKeys, ticketKeys } from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { PLATFORM_USER_ACTIONS } from "../../../../utils/constants";
import { icons } from "../../environment/icons/iconExport";
import IntegrationImage from "../components/IntegrationImage";
import { useJiraQueries } from "./hooks/useJiraQueries";

export default function JiraButton({
  fullControl,
  vulnerability,
  isPerMisconfiguration = false,
  selectedMisconfigurations = [],
  createJiraIssue,
  editJiraIssue,
  linkJiraIssue,
  jiraTicketLink,
  loading,
  sx,
  hideTitle = false,
  hideTooltip = false,
  hideLinkIcon = false,
  disabled = false,
}) {
  const colors = useColors();
  const {
    trackEvent,
    deleteJiraIssueByControlId,
    deleteJiraIssueByVulnerabilityId,
    deleteJiraIssuePerMisconfiguration,
    getMisconfigurationsWithTickets,
    unlinkTicket,
  } = useAPI();
  const [isRemoveLinkDialogOpen, setIsRemoveLinkDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [jiraIssueExpanded, setJiraIssueExpanded] = useState(false);
  const queryClient = useQueryClient();

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_TICKET
  );
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jira");
  const { jsmIsAgent, jsmIsAgentIsSuccess } = useJiraQueries();
  // NOTE: Fetch misconfiguration IDs with tickets so we can invalidate it when the user deletes a Jira issue and the data will refetch before the Create Jira Issue form is opened
  const { data: misconfigurationIdsWithTickets } = useQuery({
    queryKey: jiraIssuesKeys.jiraMisconfigurationsWithTickets(
      fullControl?.control_id
    ),
    queryFn: () => getMisconfigurationsWithTickets(fullControl?.control_id),
    select: (response) => response.data,

    enabled:
      fullControl !== null &&
      !vulnerability &&
      isIntegrated &&
      !isIntegratedLoading,
  });

  const handleCreateJiraIssue = () => {
    const identifier = vulnerability
      ? `vulnerability: ${vulnerability?.vulnerability_id}`
      : `control: ${fullControl?.control_id}`;
    const message = `User clicked on 'Create Jira Issue' button for ${identifier}`;
    trackEvent(message).then();
    handleCloseMenu();

    createJiraIssue();
  };

  const handleJiraIssueClick = (event) => {
    const message = "Dropdown menu for 'Jira Issue' button was opened";
    trackEvent(message).then();

    setAnchorEl(event.currentTarget);
    setJiraIssueExpanded(true);
  };

  const handleLinkButton = () => {
    const message =
      "User clicked on 'Link' button under 'Jira Issue' dropdown menu";
    trackEvent(message).then();
    linkJiraIssue();
    handleCloseMenu();
  };

  const handleViewButton = () => {
    const message =
      "User clicked on 'View' button under 'Jira Issue' dropdown menu";
    trackEvent(message).then();

    handleCloseMenu();
    window.open(jiraTicketLink);
  };

  const handleEdit = () => {
    const message =
      "User clicked on 'Edit' button under 'Jira Issue' dropdown menu";
    trackEvent(message).then();

    editJiraIssue();
    handleCloseMenu();
  };

  const handleDeleteButton = () => {
    const message =
      "User clicked on 'Delete' button under 'Jira Issue' dropdown menu";
    trackEvent(message).then();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirmButton = async () => {
    const message = "User clicked on 'Confirm' button for deleting Jira ticket";
    trackEvent(message).then();
    Toast.info(`Deleting Jira issue...`);

    try {
      if (vulnerability) {
        await deleteJiraIssueByVulnerabilityId(vulnerability.vulnerability_id);
      } else if (isPerMisconfiguration) {
        const configIds = selectedMisconfigurations.map(
          (misconfiguration) => misconfiguration.misconfiguration_id
        );
        await deleteJiraIssuePerMisconfiguration(configIds);
      } else {
        await deleteJiraIssueByControlId(fullControl.control_id);
      }
      Toast.success(`Jira issue deleted successfully!`);
      queryClient.invalidateQueries({
        queryKey: jiraIssuesKeys.all,
      });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    } catch (error) {
      Toast.error("Deleting Jira issue failed, please try again later.");
    }

    handleCloseDialog();
  };

  const handleRemoveLink = async () => {
    const message = "User clicked on 'Remove' button for removing Jira link";
    trackEvent(message).then();
    Toast.info(`Removing Jira issue link...`);

    try {
      let controlId = fullControl?.control_id || null;
      let misconfigurationId = null;
      let vulnerabilityId = null;

      if (vulnerability) {
        vulnerabilityId = vulnerability.vulnerability_id;
      } else if (isPerMisconfiguration) {
        misconfigurationId = selectedMisconfigurations[0]?.misconfiguration_id;
      }

      await unlinkTicket(controlId, misconfigurationId, vulnerabilityId);
      Toast.success(`Jira issue link removed successfully!`);
      queryClient.invalidateQueries({ queryKey: jiraIssuesKeys.all });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    } catch (error) {
      Toast.error("Removing Jira issue link failed, please try again later.");
    }

    handleCloseDialog("removeLink");
  };

  const handleCloseDialog = (action) => {
    let message = "";
    if (action === "removeLink") {
      setIsRemoveLinkDialogOpen(false);
      message = "Confirmation dialog for remove Jira issue link was closed";
    } else {
      setIsDeleteDialogOpen(false);
      message = "Confirmation dialog for delete Jira issue was closed";
    }
    trackEvent(message).then();

    handleCloseMenu();
  };

  const handleCloseMenu = () => {
    const message = "Dropdown menu for 'Jira Issue' button was closed";
    trackEvent(message).then();

    setAnchorEl(null);
    setJiraIssueExpanded(false);
  };

  const handleRemoveLinkButton = () => {
    const message =
      "User clicked on 'Remove Link' button under 'Jira Issue' dropdown menu";
    trackEvent(message).then();
    setIsRemoveLinkDialogOpen(true);
  };

  const actions = {
    View: {
      handler: handleViewButton,
      icon: (
        <RemoveRedEyeRounded
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
    },
    // TODO: Add Edit capabilities to Jira issue modal https://linear.app/breeze-security/issue/DEV-3294/add-edit-capabilities-to-jira-issue-modal
    // Edit: {
    //   handler: handleEdit,
    //   icon: (
    //     <EditRounded sx={{ fontSize: "20px", color: colors.icon_action_active }} />
    //   ),
    //   showWhenThereIsIssue: true,
    //   disabled: !hasPermission,
    // },
    "Create New": {
      handler: handleCreateJiraIssue,
      icon: (
        <AddCircleOutlineRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: false,
      disabled: !hasPermission,
    },
    "Link Existing": {
      handler: handleLinkButton,
      icon: (
        <AddLinkRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: false,
      disabled: !hasPermission,
    },
    "Remove Link": {
      handler: handleRemoveLinkButton,
      icon: (
        <LinkOffRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
      disabled: !hasPermission,
    },
    "Delete Issue": {
      handler: handleDeleteButton,
      icon: (
        <SvgToIcon
          IconLogo={icons.DeleteBinIcon}
          width={"16px"}
          style={{ color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
      disabled: !hasPermission || !jsmIsAgent || !jsmIsAgentIsSuccess,
    },
  };

  return (
    <>
      <CustomToolTip title={hideTooltip ? "" : "Jira Issue"} placement="top">
        <IconButton
          sx={{
            height: "32px",
            width: jiraTicketLink ? "auto" : "32px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            borderRadius: "10px",
            border: "1px solid " + colors.grey_300,
            "&:hover": {
              background: colors.grey_8percent,
              borderColor: colors.gray_main,
            },
            "&.Mui-disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
            },
            ...sx,
          }}
          onClick={handleJiraIssueClick}
          disabled={loading || disabled}
        >
          {loading ? (
            <CircularProgress size="1rem" sx={{ color: colors.primary_main }} />
          ) : (
            <>
              <IntegrationImage
                name={"jira"}
                width={"16px"}
                newLogo={true}
                showToolTip={false}
              />
              {jiraTicketLink && !hideLinkIcon && (
                <InsertLinkRoundedIcon
                  sx={{
                    width: "16px",
                    color: colors.action_active,
                  }}
                />
              )}
              {!hideTitle && (
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  width={"100%"}
                  sx={{ paddingLeft: "2px" }}
                >
                  <Typography variant="caption" color={colors.text_primary}>
                    {"Jira Issue"}
                  </Typography>
                  <ChevronRight sx={{ width: "16px" }} />
                </Box>
              )}
            </>
          )}
        </IconButton>
      </CustomToolTip>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            width: "146px",
            borderRadius: "9px",
            boxShadow: colors.dropdown_menu_shadow,
            border: "1px solid" + colors.primary_48percent,
            padding: "4px",
          },
        }}
      >
        {Object.keys(actions)
          .filter(
            (actionName) =>
              actions[actionName].showWhenThereIsIssue ===
              (jiraTicketLink !== null)
          )
          .map((actionName) => (
            <CustomToolTipWithPermission
              title={null}
              permission={!actions[actionName].disabled}
            >
              <MenuItem
                onClick={actions[actionName].handler}
                sx={{
                  color: colors.subtitle,
                  padding: 1,
                  gap: 1,
                }}
                disabled={actions[actionName].disabled || disabled}
              >
                {actions[actionName].icon}
                <Typography variant="caption" style={{ textTransform: "none" }}>
                  {actionName}
                </Typography>
              </MenuItem>
            </CustomToolTipWithPermission>
          ))}
      </Menu>

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        openDialog={isDeleteDialogOpen}
        handleDialogClose={() => handleCloseDialog("delete")}
        handleConfirm={handleDeleteConfirmButton}
        dialogTitle="Delete Jira Issue"
        dialogContentFirstLine="Are you sure you want to delete the issue?"
        confirmationButtonText="Delete"
        destructive={true}
      />

      {/* Remove Link confirmation dialog */}
      <ConfirmationDialog
        openDialog={isRemoveLinkDialogOpen}
        handleDialogClose={() => handleCloseDialog("removeLink")}
        handleConfirm={handleRemoveLink}
        dialogTitle="Remove Jira Issue Link"
        dialogContentFirstLine="Are you sure you want to remove the link?"
        confirmationButtonText="Remove"
        destructive={true}
      />
    </>
  );
}
